import React, {
    useCallback,
    useEffect,
    useState
} from 'react';
import axios from 'axios';
import {
    Button,
    Card,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Space,
    Tag,
    Typography,
    message
} from 'antd';
import {
    ReloadOutlined,
    RollbackOutlined
} from '@ant-design/icons';

const { Text } = Typography;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

const statusTag = row => {
    if (row.status === 'return_pending') {
        return (
            <Tag color="orange">ожидает приёмки складом</Tag>
        );
    }
    if (row.status === 'on_object') {
        return (
            <Tag color="green">
                на объекте
                {row.task?.external_id
                    ? ` · ${row.task.external_id}`
                    : row.task_id
                      ? ` · заявка #${row.task_id}`
                      : ''}
            </Tag>
        );
    }
    return <Tag color="blue">числится на мне</Tag>;
};

const InstallerTmcPanel = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [returnRow, setReturnRow] = useState(null);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get('/api/tmc/my', {
                headers: authHeaders()
            });
            setRows(data.data || []);
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Не удалось загрузить ТМЦ'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const submitReturn = async values => {
        try {
            await axios.post(
                `/api/tmc/my/${returnRow.id}/return`,
                {
                    quantity: values.quantity,
                    note: values.note
                },
                { headers: authHeaders() }
            );
            message.success(
                'Запрос на возврат отправлен. Позиция будет висеть до приёмки складом.'
            );
            setReturnRow(null);
            form.resetFields();
            load();
        } catch (error) {
            message.error(
                error.response?.data?.message || error.message
            );
        }
    };

    const held = rows.filter(r => r.status === 'held');
    const onObject = rows.filter(r => r.status === 'on_object');
    const pending = rows.filter(
        r => r.status === 'return_pending'
    );

    const renderList = (list, emptyText) => {
        if (!list.length) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={emptyText}
                />
            );
        }

        return list.map(row => (
            <div
                key={row.id}
                style={{
                    padding: '12px 0',
                    borderBottom: '1px solid #f0f0f0'
                }}
            >
                <Space
                    style={{
                        width: '100%',
                        justifyContent: 'space-between'
                    }}
                    align="start"
                >
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>
                            {row.item?.name || '—'}
                        </div>
                        <Text
                            type="secondary"
                            style={{ fontSize: 12 }}
                        >
                            {row.quantity}{' '}
                            {row.item?.unit || ''}
                            {row.item?.sku
                                ? ` · ${row.item.sku}`
                                : ''}
                        </Text>
                        <div style={{ marginTop: 6 }}>
                            {statusTag(row)}
                        </div>
                    </div>
                    {row.status === 'held' ? (
                        <Button
                            size="small"
                            icon={<RollbackOutlined />}
                            onClick={() => {
                                setReturnRow(row);
                                form.setFieldsValue({
                                    quantity: Number(
                                        row.quantity
                                    ),
                                    note: ''
                                });
                            }}
                        >
                            Вернуть
                        </Button>
                    ) : null}
                </Space>
            </div>
        ));
    };

    return (
        <Card
            size="small"
            title="Моё ТМЦ"
            loading={loading}
            extra={
                <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={load}
                >
                    Обновить
                </Button>
            }
        >
            <Text
                type="secondary"
                style={{ display: 'block', marginBottom: 12 }}
            >
                «На объекте» — установлено по заявке, но до
                сдачи отчёта остаётся на вас. После отчёта
                списывается автоматически.
            </Text>

            <Text strong>В наличии</Text>
            {renderList(held, 'Нет свободного ТМЦ')}

            <Text
                strong
                style={{ display: 'block', marginTop: 16 }}
            >
                На объектах
            </Text>
            {renderList(onObject, 'Нет ТМЦ на объектах')}

            <Text
                strong
                style={{ display: 'block', marginTop: 16 }}
            >
                Ожидает возврата на склад
            </Text>
            {renderList(pending, 'Нет ожидающих возвратов')}

            <Modal
                title="Вернуть на склад"
                open={Boolean(returnRow)}
                onCancel={() => {
                    setReturnRow(null);
                    form.resetFields();
                }}
                onOk={() => form.submit()}
                okText="Отправить запрос"
                destroyOnClose
            >
                <Space
                    direction="vertical"
                    style={{ width: '100%' }}
                >
                    <Text>
                        {returnRow?.item?.name} — сейчас{' '}
                        {returnRow?.quantity}{' '}
                        {returnRow?.item?.unit}
                    </Text>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={submitReturn}
                    >
                        <Form.Item
                            name="quantity"
                            label="Количество к возврату"
                            rules={[{ required: true }]}
                        >
                            <InputNumber
                                min={0.001}
                                max={Number(
                                    returnRow?.quantity || 0
                                )}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="note"
                            label="Комментарий"
                        >
                            <Input.TextArea rows={2} />
                        </Form.Item>
                    </Form>
                </Space>
            </Modal>
        </Card>
    );
};

export default InstallerTmcPanel;
